import { useMemo } from 'react';
import type { LabTestTVitalLabTestMetadataTVitalLabMarkerMetadataT as LabTestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useSearchLabTestsQuery } from '@/entities/labs';

/**
 * Resolve a full LabTestT (including aoe_schema) by its ID.
 *
 * Uses the search endpoint with an empty filter to fetch all available tests,
 * then matches by ID. The result is cached by React Query, so subsequent
 * calls with the same ID are essentially free.
 *
 * Pass `undefined` to disable the query.
 */
export function useResolveLabTest(labTestId: string | undefined): {
  labTest: LabTestT | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useSearchLabTestsQuery(labTestId ? {} : undefined);

  const labTest = useMemo(() => {
    if (!labTestId || !query.data) return undefined;
    return query.data.labTests.find((t) => t.id === labTestId);
  }, [labTestId, query.data]);

  return {
    labTest,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
